import React, { useContext, useState } from 'react';
import AdminLayout from '@app/layout/AdminLayout.tsx';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { ApiContext } from '@api/api';
import { RingilHeading } from '@components/styles.tsx';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import {
  WebhookTestRequestInDTO,
  WebhookTestPreviewOutDTO,
  WebhookTypeEnum,
  WebhookActionTypeEnum,
  WebhookDefinitionOutDTO,
  WebhookDefinitionFilter,
  Pageable,
  PageResponseWebhookDefinitionOutDTO,
} from '@api/ringil-api.v2.tsx';

const webhookTypeOptions = Object.values(WebhookTypeEnum);
const actionTypeOptions: Array<{ label: string; value: WebhookActionTypeEnum | null }> = [
  { label: '(none)', value: null },
  ...Object.values(WebhookActionTypeEnum).map(v => ({ label: v, value: v })),
];

interface WebhookTestForm {
  entityId: string;
  webhookType: WebhookTypeEnum | null;
  actionType: string | null;
  cameraId: string;
}

const IndexPage: React.FC = () => {
  const { tr } = useTranslationLgs();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<WebhookTestPreviewOutDTO | null>(null);

  const { adminWebhookPreview, adminWebhookListDefinitions } = useContext(ApiContext);

  const schema = yup.object().shape({
    entityId: yup.string().required(),
    webhookType: yup.string().required(),
  });

  const form = useForm<WebhookTestForm>(
    schema,
    { entityId: '', webhookType: null, actionType: null, cameraId: '' },
    () => handlePreview(),
    false,
    false,
  );

  const handlePreview = () => {
    setLoading(true);
    setResult(null);

    const request = {
      entityId: form.values.entityId,
      webhookType: form.values.webhookType!,
      webhookActionType: form.values.actionType || undefined,
      cameraId: form.values.webhookType === WebhookTypeEnum.RESERVATION_OPEN_GATE_MESSAGE ? form.values.cameraId : undefined,
    } as WebhookTestRequestInDTO;

    adminWebhookPreview(
      request,
      (data) => {
        setResult(data);
        setLoading(false);
      },
      null,
      {
        onError: (_error, showStandardError) => {
          setLoading(false);
          showStandardError();
        },
        onValidationFailed: () => setLoading(false),
      },
    );
  };

  const formatJson = (json: string): string => {
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  };

  const loadDataApi = (
    filter: WebhookDefinitionFilter,
    pageable: Pageable,
    onSuccess: (pagedData: PagedData<WebhookDefinitionOutDTO>) => void,
  ) => {
    adminWebhookListDefinitions(
      pageable,
      filter,
      (response: PageResponseWebhookDefinitionOutDTO) => {
        onSuccess({ data: response.data, total: response.total, page: response.page, size: response.size });
      },
    );
  };

  const columnsConfig: ColumnConfig<WebhookDefinitionOutDTO>[] = [
    { id: 'companyName', header: 'Company', headerDefault: 'Company', width: '20%', sortable: false, field: 'companyName', order: 1, render: row => <div>{row.companyName}</div> },
    { id: 'webhookType', header: 'Webhook Type', headerDefault: 'Webhook Type', width: '20%', sortable: false, field: 'webhookType', order: 2, render: row => <div>{row.webhookType}</div> },
    { id: 'url', header: 'URL', headerDefault: 'URL', width: '35%', sortable: false, order: 3, render: row => <div className={'truncate text-sm'}>{row.url}</div> },
    { id: 'implementationType', header: 'Impl. Type', headerDefault: 'Impl. Type', width: '12%', sortable: false, order: 4, render: row => <div>{row.implementationType}</div> },
    { id: 'authenticationType', header: 'Auth Type', headerDefault: 'Auth Type', width: '13%', sortable: false, order: 5, render: row => <div>{row.authenticationType}</div> },
  ];

  const filtersConf: FilterConfig[] = [
    { filterType: FilterType.InputText, placeholder: 'Filter by company', value: ['companyName'] },
    { filterType: FilterType.InputText, placeholder: 'Filter by webhook type', value: ['webhookType'] },
  ];

  return (
    <AdminLayout title={'Webhook Tests'}>
      <RingilHeading>Webhook Tests</RingilHeading>

      <div className={'mt-6'}>
        <COHeadline title={'Configured webhooks'} variant={'h2'} className={'mb-4'} />
        <RingilDataTable
          columnConfig={columnsConfig}
          loadDataFromApi={loadDataApi}
          filterName={'admin_webhook_definitions_list'}
          filtersConf={filtersConf}
        />
      </div>

      <div className={'flex flex-col gap-1 mt-8 w-[400px]'}>
        <COHeadline title={'Preview webhook payload'} variant={'h2'} className={'mb-4'} />

        <Field
          label={'Entity ID (Expedition or Reservation)'}
          required={!form.values.entityId}
          errorMessage={findValidationMessage(form.validationErrors, form.names.entityId, tr)}
        >
          <RingilInputText
            value={form.values.entityId}
            onChange={value => form.setFieldValue('entityId', value)}
            fullWidth
          />
        </Field>

        <Field
          label={'Webhook Type'}
          required={!form.values.webhookType}
          errorMessage={findValidationMessage(form.validationErrors, form.names.webhookType, tr)}
        >
          <Dropdown
            value={form.values.webhookType}
            options={webhookTypeOptions}
            onChange={e => form.setFieldValue('webhookType', e.value)}
            placeholder={'Select webhook type'}
            className={'w-full'}
          />
        </Field>

        <Field label={'Action Type (optional)'}>
          <Dropdown
            value={form.values.actionType}
            options={actionTypeOptions}
            onChange={e => form.setFieldValue('actionType', e.value)}
            placeholder={'Select action type'}
            className={'w-full'}
          />
        </Field>

        {form.values.webhookType === WebhookTypeEnum.RESERVATION_OPEN_GATE_MESSAGE && (
          <Field label={'Camera ID'} required={!form.values.cameraId}>
            <RingilInputText
              value={form.values.cameraId}
              onChange={value => form.setFieldValue('cameraId', value)}
              fullWidth
            />
          </Field>
        )}

        <div className={'w-[300px] mt-4'}>
          <RingilButton
            label={'Preview'}
            onClick={() => form.validateAndSend()}
            mode={'filled'}
            size={'big'}
            loading={loading}
          />
        </div>
      </div>

      {result && (
        <div className={'mt-8'}>
          <COHeadline title={'Result'} variant={'h2'} className={'mb-4'} />

          <div className={'flex flex-col gap-2 mb-4'}>
            <div><strong>Webhook Type:</strong> {result.webhookType}</div>
            <div><strong>Webhook URL:</strong> {result.webhookUrl || 'N/A'}</div>
            <div><strong>Implementation Type:</strong> {result.implementationType || 'N/A'}</div>
          </div>

          <div className={'border rounded p-4 bg-gray-50 overflow-auto max-h-[600px]'}>
            <pre className={'text-sm whitespace-pre-wrap break-words'}>{formatJson(result.payloadJson)}</pre>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default withAuthenticationRequired(IndexPage);
